/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// In-chat CALL message tile — Ping-Desktop Figma. One neutral card with
// four states, selected by `state` (+ `joined` for the ongoing split):
//   • Ongoing, not joined   4080:15402  →  talking waveform, live timer, "Join the call" (#537EFF)
//   • Ongoing, joined        4081:16754  →  talking waveform, live timer, "You're in this call" (#6B7280)
//   • Ended                  4081:16499  →  green outline Phone, "Lasted …", no CTA
//   • Missed                 4081:16621  →  PhoneMissed glyph, reason, "Tap to call back" (#6B7280)
//
// Card chrome (dark / light mirror):
//   outer frame  #15171C  r12 p2 gap4   →  content  #222429  r10 px12 py8
//   status text  #F5F6F8 700/16·22      timer/subtitle  #B5BAC3 400/14·20
//   avatar stack 20×20 r4, −4 overlap, 1px stroke = content bg, "+N" #B5BAC3 400/12
//   CTA = plain text link (400/14·20) — NO fill, NO icons.
//   waveform = 4 bars 2px wide r-full, heights 6/10/14/10, fill #537EFF, animated.
//   footer (px8): NotebookText + "Meeting notes" + Vault + Download (#8C929E 16).
//   meta (bubble mode): send-time #6B7280 10/400 + double-check when own+read.
//
// className-first; inline `style` only where a class can't reach: the
// runtime-animated bar transform AND the bar dimensions/colour, because
// NativeWind does NOT thread `className` through `Animated.View` (the classes
// are silently dropped → invisible bars). Presentation-only: the HOST owns the
// live session + the join / call-back / notes callbacks.

import type React from "react";
import { useEffect, useRef, useState } from "react";
import {
	Animated,
	Easing,
	Image,
	Platform,
	Pressable,
	Text,
	View,
} from "react-native";
import {
	CheckCheck,
	Download,
	MessagesSquare,
	NotebookText,
	Phone,
	PhoneMissed,
} from "lucide-react-native";

/** A resolved participant for the avatar stack. */
export interface CallParticipantAvatar {
	readonly initials: string;
	readonly avatarUrl?: string | undefined;
}

/** Which call-tile visual the timeline event maps to. */
export type CallTileState = "ongoing" | "ended" | "missed";

/** "Meeting notes" footer wiring (the footer always renders, per Figma). */
export interface CallMeetingNotes {
	/** Footer label — defaults to "Meeting notes". */
	readonly label?: string | undefined;
	/** Open the saved call notes. */
	readonly onOpen?: (() => void) | undefined;
	/** Save the notes to the vault. */
	readonly onOpenVault?: (() => void) | undefined;
	/** Download the notes. */
	readonly onDownload?: (() => void) | undefined;
	/** The call has saved notes → show a red-dot indicator on the button. */
	readonly hasContent?: boolean | undefined;
}

export interface CallNotificationCardProps {
	/** Visual state. Defaults to "ongoing". */
	readonly state?: CallTileState | undefined;
	/** Status line — "Call ongoing" / "Call ended" / "Call missed". */
	readonly status: string;
	/** Subtitle fallback when no live timer applies (e.g. "Lasted 10 minutes."). */
	readonly durationLabel: string;
	/**
	 * Ongoing only: epoch-ms the call started. When present the subtitle becomes
	 * a live `HH:MM:SS` timer ticking every second (Figma "00:02:32").
	 */
	readonly startedAtMs?: number | undefined;
	/** Resolved participant avatars (host-supplied) for the stack. */
	readonly participants?: ReadonlyArray<CallParticipantAvatar> | undefined;
	/** Total participants beyond the avatars shown → renders "+N". */
	readonly extraCount?: number | undefined;
	/** Aligns the tile (own → right). */
	readonly isSelf?: boolean | undefined;
	/** Ongoing only: viewer is already in the call → "You're in this call". */
	readonly joined?: boolean | undefined;
	/** Tapping the CTA joins / returns to / calls back (host-owned). */
	readonly onJoinLeave?: (() => void) | undefined;
	/** "Meeting notes" footer wiring. */
	readonly notes?: CallMeetingNotes | undefined;
	/** Open this call's thread back in the chat room (footer "Call threads"). */
	readonly onOpenCallThread?: (() => void) | undefined;
	/** The call's thread has (or had) messages → red-dot on "Call threads". */
	readonly callThreadHasMessages?: boolean | undefined;
	/** Send time, pre-formatted clock string — e.g. "13:51". */
	readonly timeLabel?: string | undefined;
	/** Own + read → renders the double-check beside the time. */
	readonly delivered?: boolean | undefined;
	/** Compact/stacked rows surface the time in the header → hide the in-tile meta. */
	readonly hideTime?: boolean | undefined;
	/**
	 * Back-compat override: `false` forces no CTA. When undefined the CTA is
	 * derived from `state` (ended → none; ongoing/missed → shown).
	 */
	readonly actionable?: boolean | undefined;
}

// "Talking" waveform — Figma 4 bars, 2px wide, r-full, heights 6/10/14/10.
const WAVE_HEIGHTS: ReadonlyArray<number> = [6, 10, 14, 10];
// Per-bar animation shape (min scaleY, half-cycle ms, start offset ms) — the
// differing durations + offsets give the equaliser an organic, non-synchronised
// pulse rather than four bars breathing in lockstep.
const WAVE_MIN: ReadonlyArray<number> = [0.45, 0.3, 0.55, 0.4];
const WAVE_DUR: ReadonlyArray<number> = [520, 440, 600, 480];
const WAVE_DELAY: ReadonlyArray<number> = [0, 120, 60, 180];

/** Pads to `HH:MM:SS`. */
function formatElapsed(ms: number): string {
	const total = Math.floor(ms / 1000);
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);
	return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** Small unread/has-content indicator dot, top-right of a footer icon. */
function RedDot(): JSX.Element {
	return (
		<View className="absolute -right-1 -top-1 h-[7px] w-[7px] rounded-full border border-[#15171C] bg-[#FF5353]" />
	);
}

/**
 * Animated talking-equaliser. Drives `scaleY` on four centred bars in an
 * infinite loop. Native driver off-web (transform-only, GPU-cheap); on
 * react-native-web the JS driver animates the same transform via rAF. Loops are
 * stopped + reset on unmount so a scrolled-away tile leaks nothing. Bar size +
 * colour live in `style` because NativeWind drops `className` on `Animated.View`.
 */
const CallWaveform: React.FC = () => {
	const bars = useRef(WAVE_HEIGHTS.map(() => new Animated.Value(1))).current;

	useEffect(() => {
		const native = Platform.OS !== "web";
		const anims = bars.map((v, i) =>
			Animated.sequence([
				Animated.delay(WAVE_DELAY[i]!),
				Animated.loop(
					Animated.sequence([
						Animated.timing(v, {
							toValue: WAVE_MIN[i]!,
							duration: WAVE_DUR[i]!,
							easing: Easing.inOut(Easing.quad),
							useNativeDriver: native,
						}),
						Animated.timing(v, {
							toValue: 1,
							duration: WAVE_DUR[i]!,
							easing: Easing.inOut(Easing.quad),
							useNativeDriver: native,
						}),
					]),
				),
			]),
		);
		anims.forEach((a) => a.start());
		return () => {
			anims.forEach((a) => a.stop());
			bars.forEach((v) => v.setValue(1));
		};
	}, [bars]);

	return (
		<View className="h-[18px] w-[18px] flex-row items-center justify-center gap-0.5">
			{WAVE_HEIGHTS.map((h, i) => (
				<Animated.View
					key={i}
					style={{
						width: 2,
						height: h,
						borderRadius: 999,
						backgroundColor: "#537EFF",
						transform: [{ scaleY: bars[i]! }],
					}}
				/>
			))}
		</View>
	);
};

export const CallNotificationCard: React.FC<CallNotificationCardProps> = ({
	state = "ongoing",
	status,
	durationLabel,
	startedAtMs,
	participants = [],
	extraCount = 0,
	isSelf = false,
	joined = false,
	onJoinLeave,
	notes,
	onOpenCallThread,
	callThreadHasMessages = false,
	timeLabel,
	delivered = false,
	hideTime = false,
	actionable,
}) => {
	// Live timer for ongoing calls (Figma "00:02:32"). Ticks once a second.
	const [nowMs, setNowMs] = useState<number>(() => Date.now());
	const live = state === "ongoing" && startedAtMs !== undefined;
	useEffect(() => {
		if (!live) return;
		const id = setInterval(() => setNowMs(Date.now()), 1000);
		return () => clearInterval(id);
	}, [live]);
	const subtitle = live
		? formatElapsed(Math.max(0, nowMs - (startedAtMs ?? nowMs)))
		: durationLabel;

	// CTA text + tone per Figma. Ended has none; ongoing splits on `joined`;
	// missed offers a call-back. `actionable === false` force-hides it.
	const ctaAllowed = actionable ?? state !== "ended";
	let ctaText: string | null = null;
	let ctaBrand = false;
	if (ctaAllowed && state === "ongoing") {
		ctaText = joined ? "You’re in this call" : "Join the call";
		ctaBrand = !joined;
	} else if (ctaAllowed && state === "missed") {
		ctaText = "Tap to call back";
	}

	const hasAvatars = participants.length > 0 || extraCount > 0;
	const showMeta = !hideTime && timeLabel !== undefined && timeLabel !== "";
	const notesLabel = notes?.label ?? "Meeting notes";

	return (
		<View
			className={`my-xs w-full min-w-[250px] max-w-[371px] rounded-[12px] bg-light-surface-secondary p-0.5 dark:bg-[#15171C] ${
				isSelf ? "self-end" : "self-start"
			}`}
			accessibilityLabel={`${status}, ${subtitle}`}
		>
			<View className="gap-1">
				{/* Content frame */}
				<View className="rounded-[10px] bg-white px-3 py-2 dark:bg-[#222429]">
					<View className="flex-row items-start gap-y-1 gap-x-6">
						{/* Left: status + subtitle */}
						<View className="min-w-0 flex-1 gap-0.5">
							<View className="h-[22px] flex-row items-center gap-1">
								{state === "ongoing" ? (
									<CallWaveform />
								) : (
									<View className="h-[18px] w-[18px] items-center justify-center">
										{state === "ended" ? (
											<Phone size={16} strokeWidth={1.5} color="#3CCB4F" />
										) : (
											<PhoneMissed size={16} strokeWidth={1.5} color="#8C929E" />
										)}
									</View>
								)}
								<Text
									className="flex-1 text-base font-bold leading-[22px] text-light-text-primary dark:text-[#F5F6F8]"
									numberOfLines={1}
								>
									{status}
								</Text>
							</View>
							<Text
								className="text-[14px] leading-[20px] text-light-text-secondary dark:text-[#B5BAC3]"
								numberOfLines={1}
							>
								{subtitle}
							</Text>
						</View>

						{/* Right: avatar stack + CTA + meta */}
						<View className="items-end gap-1">
							{hasAvatars ? (
								<View className="h-5 flex-row items-center gap-1">
									<View className="flex-row items-center">
										{participants.slice(0, 3).map((p, i) =>
											p.avatarUrl !== undefined ? (
												<Image
													key={`av:${i}`}
													source={{ uri: p.avatarUrl }}
													className={`h-5 w-5 rounded-[4px] border border-white dark:border-[#222429] ${
														i === 0 ? "" : "-ml-1"
													}`}
													accessibilityIgnoresInvertColors
												/>
											) : (
												<View
													key={`av:${i}`}
													className={`h-5 w-5 items-center justify-center rounded-[4px] border border-white bg-[#B5BAC3] dark:border-[#222429] ${
														i === 0 ? "" : "-ml-1"
													}`}
												>
													<Text className="text-[8px] font-bold text-[#15171C]">
														{p.initials.slice(0, 2)}
													</Text>
												</View>
											),
										)}
									</View>
									{extraCount > 0 ? (
										<Text className="text-xs leading-[17px] text-light-text-secondary dark:text-[#B5BAC3]">
											{`+${extraCount}`}
										</Text>
									) : null}
								</View>
							) : null}

							{ctaText ? (
								<Pressable
									accessibilityRole="button"
									accessibilityLabel={ctaText}
									onPress={onJoinLeave}
									className="rounded-[6px] px-1 py-0.5 active:opacity-70"
								>
									<Text
										className={`text-[14px] leading-[20px] ${
											ctaBrand ? "text-[#537EFF]" : "text-[#6B7280]"
										}`}
									>
										{ctaText}
									</Text>
								</Pressable>
							) : null}

							{showMeta ? (
								<View className="flex-row items-center gap-0.5">
									<Text className="text-[10px] leading-[14px] text-[#6B7280]">
										{timeLabel}
									</Text>
									{isSelf && delivered ? (
										<CheckCheck size={12} strokeWidth={2} color="#6B7280" />
									) : null}
								</View>
							) : null}
						</View>
					</View>
				</View>

				{/* Footer — Figma "Meeting notes" row (present in every state). */}
				<View className="flex-row items-center gap-y-1 gap-x-6 px-2 pb-0.5">
					<Pressable
						accessibilityRole="button"
						accessibilityLabel="Open meeting notes"
						onPress={notes?.onOpen}
						className="min-w-0 flex-1 flex-row items-center gap-1 active:opacity-70"
					>
						<View>
							<NotebookText size={16} strokeWidth={1.5} color="#8C929E" />
							{notes?.hasContent ? <RedDot /> : null}
						</View>
						<Text
							className="min-w-0 flex-1 text-[14px] leading-[20px] text-light-text-secondary dark:text-[#B5BAC3]"
							numberOfLines={1}
						>
							{notesLabel}
						</Text>
					</Pressable>
					<View className="flex-row items-center gap-2">
						{/* "Save notes to vault" is a future feature — hidden until the
						    vault wiring lands. Keep the layout slot for when it returns.
						<Pressable
							accessibilityRole="button"
							accessibilityLabel="Save notes to vault"
							onPress={notes?.onOpenVault}
							className="active:opacity-70"
						>
							<Vault size={16} strokeWidth={1.5} color="#8C929E" />
						</Pressable>
						*/}
						{/* Call threads — revisit this call's thread in the chat room. */}
						<Pressable
							accessibilityRole="button"
							accessibilityLabel="Open call threads"
							onPress={onOpenCallThread}
							className="active:opacity-70"
						>
							<View>
								<MessagesSquare size={16} strokeWidth={1.5} color="#8C929E" />
								{callThreadHasMessages ? <RedDot /> : null}
							</View>
						</Pressable>
						<Pressable
							accessibilityRole="button"
							accessibilityLabel="Download notes"
							onPress={notes?.onDownload}
							className="active:opacity-70"
						>
							<Download size={16} strokeWidth={1.5} color="#8C929E" />
						</Pressable>
					</View>
				</View>
			</View>
		</View>
	);
};
